import { useState, useEffect, useRef } from 'react';

// components
import Image from 'next/image';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import VisibleLoading from '@/components/utilities/VisibleLoading';

// utilities
import { getImagePath } from '@/utils/images';

const CARD_WIDTH = 232;
const GRID_GAP = 16;
const FIT_BUFFER = 16;
const XL_BREAKPOINT = 1280;

const FeaturesChecklistCarousel = ({ items, descriptionMinHeight }) => {
  const itemList = items || [];
  const wrapperRef = useRef(null);
  const [useCarousel, setUseCarousel] = useState(itemList.length > 5);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onResize = () => {
      const isXl = window.innerWidth >= XL_BREAKPOINT;
      // grid extends 8px each side via xl:-mx-2 at xl, has 20px each side padding (px-5) below xl
      const available = isXl ? el.offsetWidth + 16 : el.offsetWidth - 40;
      const needed = itemList.length * CARD_WIDTH + Math.max(0, itemList.length - 1) * GRID_GAP + FIT_BUFFER;
      setUseCarousel(needed > available);
    };
    onResize();
    const observer = new ResizeObserver(onResize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [itemList.length]);

  return (
    <div ref={wrapperRef} className="mx-auto my-10 w-[100vw] xl:w-(--container-width)">
      <VisibleLoading delay={300}>
        {({ ref, className }) => (
          <div ref={ref} className={`my-8 w-full xl:my-16 ${className || ''}`.trim()}>
            {useCarousel ? (
              <FeaturesCarousel items={itemList} descriptionMinHeight={descriptionMinHeight} />
            ) : (
              <FeaturesGrid items={itemList} descriptionMinHeight={descriptionMinHeight} />
            )}
          </div>
        )}
      </VisibleLoading>
    </div>
  );
};
export default FeaturesChecklistCarousel;

const FeaturesCarousel = ({ items, descriptionMinHeight }) => {
  const [api, setApi] = useState();
  const [current, setCurrent] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  useEffect(() => {
    if (!api) {
      return;
    }

    const updateScrollSnaps = () => {
      setScrollSnaps(api.scrollSnapList());
    };

    // Initialize state from external carousel API
    // setTimeout defers state updates to avoid cascading renders
    setTimeout(() => {
      updateScrollSnaps();
      setCurrent(api.selectedScrollSnap() + 1);
    }, 0);

    // Subscribe to carousel events (external system updates)
    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap() + 1);
    };

    api.on('select', handleSelect);

    const handleResize = () => {
      setTimeout(updateScrollSnaps, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      api.off('select', handleSelect);
      window.removeEventListener('resize', handleResize);
    };
  }, [api]);

  const onSelectItem = (e) => {
    const index = Number(e.currentTarget.dataset.id) || 0;

    if (api) {
      api.scrollTo(index);
    }
  };

  return (
    <>
      <Carousel setApi={setApi} opts={{ align: 'center' }}>
        <CarouselContent className="py-8 lg:py-10">
          {items.map((item, index) => {
            const isFirst = index === 0;
            const isLast = index === items.length - 1;
            return (
              <CarouselItem
                key={item.id || index}
                className={`xl:max-w-auto flex max-w-[280px] flex-col xl:basis-1/5 ${isFirst ? 'pl-7 xl:pl-7' : 'pl-4 xl:pl-4'}${isLast ? 'pr-3 xl:pr-3' : ''}`}
              >
                <ChecklistCard item={item} descriptionMinHeight={descriptionMinHeight} />
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>

      {scrollSnaps.length > 1 && (
        <div className="mt-5 flex justify-center gap-5 lg:mt-10 lg:gap-8">
          {scrollSnaps.map((_, index) => (
            <div
              key={index}
              data-id={index}
              className="group flex cursor-pointer items-center justify-center p-2"
              onClick={onSelectItem}
            >
              <div
                className={`h-[8px] w-[8px] cursor-pointer rounded-full lg:h-[10px] lg:w-[10px] ${current === index + 1 ? 'bg-brand-400' : 'bg-grey-500'} `}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const FeaturesGrid = ({ items, descriptionMinHeight }) => (
  <div className="flex flex-wrap justify-center gap-4 px-5 py-8 lg:py-10 xl:-mx-2 xl:px-0">
    {items.map((item, index) => (
      <div key={item.id || index} className="flex w-full max-w-[232px] flex-col">
        <ChecklistCard item={item} descriptionMinHeight={descriptionMinHeight} />
      </div>
    ))}
  </div>
);

const COLOR_STYLES = {
  brand: { badge: 'bg-brand-500', stroke: 'stroke-brand-500' },
  green: { badge: 'bg-green-500', stroke: 'stroke-green-500' },
  blue: { badge: 'bg-blue-500', stroke: 'stroke-blue-500' },
  yellow: { badge: 'bg-yellow-500', stroke: 'stroke-yellow-500' },
  purple: { badge: 'bg-purple-500', stroke: 'stroke-purple-500' },
  red: { badge: 'bg-red-500', stroke: 'stroke-red-500' }
};

const ChecklistCard = ({ item, descriptionMinHeight }) => {
  const { badge, stroke } = COLOR_STYLES[item.color] || COLOR_STYLES.brand;

  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-3xl bg-white p-6 shadow-2xl/15">
      <svg
        className="pointer-events-none absolute top-0 right-0 z-0"
        width="200"
        height="200"
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle className={stroke} opacity="0.1" cx="200" cy="0" r="150" />
        <circle className={stroke} opacity="0.25" cx="200" cy="0" r="105" />
        <circle className={stroke} opacity="0.4" cx="200" cy="0" r="60" />
      </svg>

      <div className="relative z-1 mb-6 flex justify-end">
        <div className={`flex h-10 w-10 items-center justify-center rounded-full text-white ${badge}`}>
          {item.icon && (
            <Image
              src={getImagePath(item.icon)}
              width={item.icon.width}
              height={item.icon.height}
              alt={item.title || ''}
              sizes="20px"
              className="h-5 w-5 object-contain"
            />
          )}
        </div>
      </div>

      <div
        className="relative z-1"
        style={descriptionMinHeight ? { minHeight: `${descriptionMinHeight}px` } : undefined}
      >
        <h3 className="text-xl font-light tracking-tight lg:text-2xl">{item.title}</h3>

        {item.description && <p className="text-grey-900 mt-3 mb-4 text-sm lg:text-base">{item.description}</p>}
      </div>

      {item.features?.length > 0 && (
        <div className="relative z-1 flex flex-col gap-3">
          {item.features.map((feature, index) => (
            <div key={index} className="text-grey-700 flex items-start gap-3 text-left">
              {feature.checked ? (
                <svg
                  className="mt-0.5 min-w-5"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="24" height="24" rx="12" fill="#D1FADF" />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M17.098 7.39016L9.93797 14.3002L8.03797 12.2702C7.68797 11.9402 7.13797 11.9202 6.73797 12.2002C6.34797 12.4902 6.23797 13.0002 6.47797 13.4102L8.72797 17.0702C8.94797 17.4102 9.32797 17.6202 9.75797 17.6202C10.168 17.6202 10.558 17.4102 10.778 17.0702C11.138 16.6002 18.008 8.41016 18.008 8.41016C18.908 7.49016 17.818 6.68016 17.098 7.38016V7.39016Z"
                    fill="#12B76A"
                  />
                </svg>
              ) : (
                <svg
                  className="mt-0.5 min-w-5"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="24" height="24" rx="12" fill="#E7E7E6" />
                  <path
                    d="M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5"
                    stroke="#B0B0B0"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              <span className="text-sm tracking-tight lg:text-base">{feature.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
